import type { Campaign, Maybe, TemplateSource, Variant } from '~/types/graphql';
import type { IVariantType } from '~/modules/campaign';
import type { IVariantMetadata } from '../types';

import {
  ASSIGNED_PAGE_IS_SELECT_MULTIPLE,
  GP_ONE_TO_ONE_TEMPLATE_TYPES,
  SHOPIFY_PROVIDER_SOURCES,
} from '../constants';
import { regexTemplateOrigin } from '~/constants/templateRegex';
import { ProviderSource, TemplateType } from '~/types/template';
import { getVariantType } from '~/modules/campaign';
import { isTemplateGP } from '~/utils';

type WinnerInfo = {
  winnerVariantKey?: IVariantType;
  hasWinner?: boolean;
};

export const mapProviderSourceToTemplateSource = (
  providerSource: ProviderSource,
): TemplateSource => {
  switch (providerSource) {
    case ProviderSource.GEMPAGESV7:
      return 'GEMPAGES';
    default:
      return 'SHOPIFY';
  }
};

export const isTemplateTypeMatched = (
  operator: 'IS' | 'IS_NOT',
  ruleTemplateType: TemplateType,
  controlTemplateType: TemplateType,
) => {
  if (operator === 'IS') {
    return ruleTemplateType === controlTemplateType;
  }

  return ruleTemplateType !== controlTemplateType;
};

export const checkDefaultTemplate = (templateSuffix?: string): boolean => {
  if (!templateSuffix) return false;

  return regexTemplateOrigin.test(templateSuffix);
};

export const isGPLandingAndArticle = (
  templateType: TemplateType,
  providerSource: ProviderSource,
) => {
  if (!isTemplateGP(providerSource)) return false;

  return GP_ONE_TO_ONE_TEMPLATE_TYPES.includes(templateType);
};

export const isTemplateShopify = (providerSource: ProviderSource) => {
  return SHOPIFY_PROVIDER_SOURCES.includes(providerSource);
};

export const isBothShopify = (control: IVariantMetadata, variant: IVariantMetadata) => {
  return isTemplateShopify(control.providerSource) && isTemplateShopify(variant.providerSource);
};

export const needMakeDefault = (
  control: IVariantMetadata | undefined,
  variant: IVariantMetadata | undefined,
) => {
  if (!control || !variant) return false;

  const isLandingAndArticle =
    control && GP_ONE_TO_ONE_TEMPLATE_TYPES.includes(control.templateType);
  if (isLandingAndArticle && isTemplateGP(variant.providerSource)) return false;

  if (isBothShopify(control, variant) && control.templateType !== TemplateType.HOME) return false;

  return control.isDefaultTemplate;
};

export const isBothGPTemplate = (
  control: IVariantMetadata | undefined,
  variant: IVariantMetadata | undefined,
): boolean => {
  return isTemplateGP(control?.providerSource) && isTemplateGP(variant?.providerSource);
};

export const needRepublishGP = (
  control: IVariantMetadata | undefined,
  variant: IVariantMetadata | undefined,
  isManualMakeDefault: boolean,
): boolean => {
  if (!control || !variant) return false;

  if (isBothGPTemplate(control, variant)) {
    return true;
  }

  const differentTemplateType = control.templateType !== variant.templateType;
  const isMakeDefault = needMakeDefault(control, variant) || isManualMakeDefault;

  return isTemplateGP(variant.providerSource) && differentTemplateType && !isMakeDefault;
};

export const isShopifyDifferentTemplateType = (
  control: IVariantMetadata | undefined,
  variant: IVariantMetadata | undefined,
): boolean => {
  if (variant?.providerSource == null) return false;

  const isVariantShopify = SHOPIFY_PROVIDER_SOURCES.includes(variant.providerSource);
  const isDifferentTemplateType = control?.templateType !== variant?.templateType;
  return isVariantShopify && isDifferentTemplateType;
};

export const getProviderSource = (
  providerSource: ProviderSource,
  defaultByProvider?: ProviderSource,
): ProviderSource => {
  if (isTemplateGP(defaultByProvider)) {
    return ProviderSource.GEMPAGESV7;
  }

  return providerSource;
};

export const hasWinningVariant = (variants?: Maybe<Variant>[]): boolean => {
  if (!variants) return false;

  return variants?.some((variant) => variant?.isWinner) ?? false;
};

export const getWinnerVariant = (variants?: Maybe<Variant>[]): Variant | undefined => {
  if (!variants) return undefined;

  return variants?.find((variant) => variant?.isWinner);
};

export const getGPEditorLocalStorageKey = (campaignId?: string) =>
  `make_winner_gp_editor_${campaignId}`;

export const getWinnerInfo = (variants?: Maybe<Variant>[]): WinnerInfo => {
  const winner = getWinnerVariant(variants);
  if (!winner) {
    return {
      winnerVariantKey: undefined,
      hasWinner: false,
    };
  }

  return {
    winnerVariantKey: getVariantType(winner),
    hasWinner: true,
  };
};

export const applyWinnerTemplateSuffix = (
  campaign: Campaign,
  winnerTemplateSuffix: string,
): Campaign => {
  return {
    ...campaign,
    variants: campaign.variants?.map((variant) =>
      variant?.isWinner ? { ...variant, winnerTemplateSuffix } : variant,
    ),
  };
};

export const getWinnerTemplateSuffix = (campaign?: Campaign) => {
  if (!campaign || !campaign.variants) return;

  const winnerVariant = campaign.variants.find((variant) => variant?.isWinner);

  return winnerVariant?.winnerTemplateSuffix;
};

export const showMakeDefaultToggle = (data: {
  control?: IVariantMetadata;
  variant?: IVariantMetadata;
}) => {
  const { control, variant } = data;
  if (!control || !variant) return false;

  const isLandingOrArticle = GP_ONE_TO_ONE_TEMPLATE_TYPES.includes(control.templateType);
  const isGPTemplate = isTemplateGP(variant.providerSource);
  if (isLandingOrArticle || !isGPTemplate) {
    return false;
  }

  const isVariantGPDefault =
    control.templateType === variant.templateType &&
    ASSIGNED_PAGE_IS_SELECT_MULTIPLE.includes(control.templateType) &&
    variant.isDefaultTemplate;
  if (isVariantGPDefault) {
    return false;
  }

  return true;
};
